import { useCallback, useState } from 'react';

import { CandidateRoleTestStatus } from '@internal/api/roles/roles';
import Routes from '@internal/config/routes';

import { isConflictError, RETAKE_UNAVAILABLE_MESSAGE } from './errors';
import { useRoleContext } from './role-context';
import { CandidateRole } from './role-types';

export interface TestLauncher {
  launchTest: (role: CandidateRole) => Promise<void>;
  startingRoleId: string | null;
  error: string | null;
  clearError: () => void;
}

export const useTestLauncher = (): TestLauncher => {
  const { startTest } = useRoleContext();
  const [startingRoleId, setStartingRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const launchTest = useCallback(
    async (role: CandidateRole) => {
      if (startingRoleId) return;
      setError(null);

      const pendingLink =
        role.assessment.test.status === CandidateRoleTestStatus.InProgress
          ? role.assessment.test.link
          : null;
      if (pendingLink) {
        window.open(pendingLink, '_blank', 'noopener');
        return;
      }

      const testTab = window.open(Routes.PreparingTest, '_blank');
      if (testTab) testTab.opener = null;

      setStartingRoleId(role.id);
      let inviteUrl: string | null = null;
      let retakeUnavailable = false;
      try {
        inviteUrl = await startTest(role.id);
      } catch (launchError) {
        retakeUnavailable = isConflictError(launchError);
      } finally {
        setStartingRoleId(null);
      }

      if (!inviteUrl) {
        testTab?.close();
        setError(
          retakeUnavailable
            ? RETAKE_UNAVAILABLE_MESSAGE
            : "We couldn't start this test. Please try again."
        );
        return;
      }

      if (testTab) {
        testTab.location.href = inviteUrl;
      } else {
        window.open(inviteUrl, '_blank', 'noopener');
      }
    },
    [startTest, startingRoleId]
  );

  return {
    launchTest,
    startingRoleId,
    error,
    clearError: () => setError(null),
  };
};
